import React, {
    useState, ChangeEvent, MouseEvent, FC, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_FILE_SIZE } from 'constants/constants';
import {
    FILE_TYPES_ALLOWED,
    MAXALLOWEDFILES,
} from '../../pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import {
    validateFile,
    validateUploadCount,
} from '../../pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/validation';

import FilePreviewItem from './FilePreviewItem';
import './UploadDocuments.css';

interface UploadDocumentsProps {
    /** Callback triggered when a validation error occurs */
    onError: (error: string) => void;
    /** Callback triggered when the file list changes successfully */
    onSuccess: (files: File[]) => void;
    /** Optional CSS class for the wrapper */
    className?: string;
    /** Maximum number of files allowed to be uploaded */
    numberOfFiles?: number;
    /** Descriptive label shown inside the upload area */
    uploadLabel?: string;
    /** Max size per file in MB */
    maxMbFileSize?: number;
    /** Custom error message for size violations */
    sizeError?: string;
    /** Custom error message for format violations */
    fileFormatError?: string;
    /** Comma-separated string of allowed MIME types */
    fileTypesAllowed?: string;
    /** Any other standard input attributes */
    [key: string]: any;
    defaultFiles?: File[];
}

const UploadDocuments: FC<UploadDocumentsProps> = ({
    onError,
    onSuccess,
    className = '',
    numberOfFiles = MAXALLOWEDFILES,
    uploadLabel = '',
    maxMbFileSize = MAX_FILE_SIZE,
    sizeError = '',
    fileFormatError = '',
    fileTypesAllowed = FILE_TYPES_ALLOWED,
    defaultFiles = undefined,
    ...rest
}) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState<File[]>(defaultFiles || []);

    useEffect(() => {
        setFiles(defaultFiles || []);
    }, [defaultFiles]);

    const removeFile = (e: MouseEvent<HTMLButtonElement>, item: File) => {
        const filteredArray = files?.filter((file) => file !== item);

        setFiles(filteredArray);
        onSuccess(filteredArray);
    };

    const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;

        if (!fileList) return null;

        const fileData = Array.from(fileList);

        onError(null);

        if (files?.length + fileData.length > numberOfFiles) {
            onError(validateUploadCount(true, numberOfFiles));

            return false;
        }

        const hasErrors: any = validateFile(fileList, {
            maxMbFileSize,
            sizeError,
            fileFormatError,
            fileTypesAllowed,
        });

        if (!hasErrors) {
            const updatedFiles = [...files, ...fileData];

            setFiles(updatedFiles);
            onSuccess(updatedFiles);
        } else {
            onError(hasErrors);
        }

        return null;
    };

    const isPdfFormat = (file: File) => file.type === 'application/pdf';

    return (
        <div className={`UploadWidget ${className}`}>
            {files.length === numberOfFiles ? null : (
                <div className="UploadWidget__Item">
                    <div className="UploadWidget__Item__Icon">
                        <span className="UploadIcon UploadIcon--Select" />
                    </div>
                    <label
                        htmlFor="FileSelect"
                        className="Upload__Label Upload__Label--FileSelect"
                    >
                        <input
                            {...rest}
                            onClick={(e) => {
                                (e.target as HTMLInputElement).value = '';
                            }}
                            onChange={onFileChange}
                            type="file"
                            id="FileSelect"
                            multiple
                            accept={fileTypesAllowed}
                            className="Upload__FileSelectInput"
                        />
                        {t('onboardingFlow.documetsPage.uploadButton')}
                        {fileTypesAllowed && (
                            <div className="UploadWidget__allowed-document-types">
                                {fileTypesAllowed
                                    .split(',')
                                    .map((f) => f.split('/')[1])
                                    .join(', ')
                                    .toUpperCase()}
                            </div>
                        )}
                        {maxMbFileSize && (
                            <div className="UploadWidget__allowed-document-size">
                                {t('uploadDocument.MaxFileSize')}
                                {' '}
                                {maxMbFileSize}
                                {' '}
                                MB
                            </div>
                        )}
                        {uploadLabel && (
                            <div className="Upload__AdditionalLabel">
                                {uploadLabel}
                            </div>
                        )}
                    </label>
                </div>
            )}

            {files?.length > 0 && (
                <div
                    className={`UploadedFilesList ${
                        files.length >= 1 && isPdfFormat(files[0])
                            ? 'SinglePDFPngPreview'
                            : 'MultipleFiles'
                    }`}
                >
                    <ul>
                        {files.map((file, index) => (
                            <FilePreviewItem
                                key={`${file.name}-${file.lastModified}-${index}`}
                                file={file}
                                onRemove={(f) => removeFile(null as any, f)}
                                t={t}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default UploadDocuments;
